import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Calendar } from '../ui/calendar';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingCalendar = ({ onBookingCreated }) => {
  const { toast } = useToast();
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [notes, setNotes] = useState('');
  const [availableSlots, setAvailableSlots] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);

  useEffect(() => {
    axios.get(`${API}/bookings/blocked-dates`)
      .then((r) => setBlockedDates(r.data.map((d) => d.date)))
      .catch(() => {});
  }, []);

  const toLocalDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const services = [
    { value: 'legal', label: 'Legal Guidance' },
    { value: 'career', label: 'Career Transition Counseling' },
    { value: 'wellbeing', label: 'Mental Wellbeing Session' },
    { value: 'financial', label: 'Tax & Investment Planning' },
    { value: 'events', label: 'Special Events' }
  ];

  const timeSlots = [
    '10:00 AM', '11:00 AM', '12:00 PM',
    '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'
  ];

  useEffect(() => {
    if (selectedService) {
      fetchAvailableSlots();
    }
  }, [selectedService]);

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get(`${API}/services/slots/${selectedService}`);
      setAvailableSlots(response.data[0]);
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedService || !selectedDate || !selectedTime) {
      toast({
        title: "Missing Information",
        description: "Please select service, date, and time",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.post(
        `${API}/bookings/`,
        {
          service_type: selectedService,
          appointment_date: selectedDate.toISOString(),
          appointment_time: selectedTime,
          notes: notes
        },
        config
      );

      toast({
        title: "Booking Confirmed!",
        description: "Confirmation email sent to your inbox.",
      });

      if (onBookingCreated) {
        onBookingCreated(response.data);
      }

      setSelectedService('');
      setSelectedDate(null);
      setSelectedTime('');
      setNotes('');
    } catch (error) {
      toast({
        title: "Booking Failed",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label htmlFor="service">Select Service *</Label>
        <select
          id="service"
          value={selectedService}
          onChange={(e) => setSelectedService(e.target.value)}
          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#e74c3c]"
          required
        >
          <option value="">Choose a service...</option>
          {services.map((service) => (
            <option key={service.value} value={service.value}>
              {service.label}
            </option>
          ))}
        </select>
      </div>

      {selectedService && (
        <>
          <div>
            <Label>Select Date *</Label>
            <div className="mt-2 border rounded-md p-4">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date() || blockedDates.includes(toLocalDateStr(date))}
                className="mx-auto"
              />
            </div>
          </div>

          {selectedDate && (
            <div>
              <Label>Select Time *</Label>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-2">
                {timeSlots.map((time) => (
                  <button
                    key={time}
                    type="button"
                    onClick={() => setSelectedTime(time)}
                    className={`px-4 py-2 rounded-md border transition-colors ${
                      selectedTime === time
                        ? 'bg-[#e74c3c] text-white border-[#e74c3c]'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-[#e74c3c]'
                    }`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific requirements or topics you would like to discuss..."
              className="mt-2"
              rows={3}
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-[#e74c3c] hover:bg-[#c0392b]"
            size="lg"
            disabled={isSubmitting || !selectedDate || !selectedTime}
          >
            {isSubmitting ? 'Booking...' : 'Confirm Booking'}
          </Button>
        </>
      )}
    </form>
  );
};

export default BookingCalendar;
